import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { httpService } from "../../../base/services/httpService.service";
import { LocalStorageService } from "../../../base/services/local-storage.service";
import * as Yup from "yup";
import { Form, Formik } from "formik";
import Toast from "../../../base/components/Toast";
import { Spin } from "antd";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { useTranslation } from "../../../i18n/LanguageContext";
import SignupLayout from "../components/SignupLayout";
import ResendCodeButton from "../components/ResendCodeButton";

const initialValues = {
   code: ""
};

function OrgVerification() {
   const { t } = useTranslation();
   const navigate = useNavigate();
   const location = useLocation();
   const userID = LocalStorageService.getItem("userId");
   const searchParam = new URLSearchParams(location.search);
   const email = searchParam.get("email");
   const [loading, setLoading] = useState(false);

   const { executeRecaptcha } = useGoogleReCaptcha();

   const [error, setError] = useState(false);

   const ConfirmCodeSchema = Yup.object().shape({
      code: Yup.string()
         .matches(/^\d{6}$/, t("code-must-have-6-digits-validation"))
         .required(t("verification-code-required-validation"))
   });

   const sentCode = async (values) => {
      if (!executeRecaptcha) {
         return;
      }

      const recaptchaToken = await executeRecaptcha("login");
      let formattedUserId = userID.replace(/"/g, "");
      try {
         setLoading(true);
         await httpService.post(
            "/Accounts/confirm-email",
            {
               userId: formattedUserId,
               code: values.code
            },
            () => {
               navigate("/auth/national-org-registration/step-three");
            },
            () => {},
            {
               headers: {
                  "g-recaptcha-response": recaptchaToken
               }
            }
         );
      } catch (error) {
         const errorMessages =
            error.response &&
            error.response.data &&
            error.response.data.errors &&
            error.response.data.errors.map((error) => error.message);
         if (errorMessages && errorMessages.length > 0) {
            errorMessages.forEach((errorMessage) => {
               Toast.error(errorMessage);
               setError(true);
            });
         } else {
            setError(false);
         }
      } finally {
         setLoading(false);
      }
   };

   const handleCodeChange = (e, setFieldValue) => {
      const value = e.target.value.replace(/\s+/g, "").replace(/[^0-9]/g, "");
      setFieldValue("code", value);
      // A new attempt clears the server's "incorrect code" message.
      setError(false);
   };

   return (
      <SignupLayout showcase="organization" backTo="/">
         <Formik
            initialValues={initialValues}
            validationSchema={ConfirmCodeSchema}
            onSubmit={(values) => sentCode(values)}
         >
            {({ setFieldValue, values, errors, isSubmitting }) => (
               <Form className="kap-org-verify">
                  <h1 className="kap-org-signup__title kap-org-verify__title">
                     {t("verification-code-title")}
                  </h1>
                  <p className="kap-org-verify__text">
                     {t("verification-code-sent-to-text")}
                     {email && <> [{email}]</>}
                  </p>

                  <input
                     className={`kap-org-verify__code${
                        errors.code || error ? " kap-org-verify__code--error" : ""
                     }`}
                     inputMode="numeric"
                     maxLength={6}
                     onChange={(e) => handleCodeChange(e, setFieldValue)}
                     value={values?.code}
                     autoComplete="one-time-code"
                     aria-label={t("verification-code-title")}
                  />
                  {error && <div className="error-message mt-2">{t("code-incorrect-text")}</div>}
                  {errors.code && <div className="error-message mt-2">{errors.code}</div>}

                  <button
                     type="submit"
                     disabled={isSubmitting || loading}
                     className="btn sign-btn font-weight-bold kap-org-verify__submit"
                  >
                     {loading ? <Spin /> : t("verify-code-button")}
                  </button>

                  <p className="kap-org-verify__help">
                     {t("didnt-receive-code-text")}{" "}
                     <ResendCodeButton email={email} />{" "}
                     {t("or-send-email-at-text")}{" "}
                     <a href="mailto:info@kaptechgroup.com">info@kaptechgroup.com</a>
                  </p>
               </Form>
            )}
         </Formik>
      </SignupLayout>
   );
}

export default OrgVerification;
